/**
 * MechaGuard - AchievementSystem (成就系统)
 *
 * 设计要点
 *   - 成就定义是**纯数据 + 纯函数谓词**（check(stats)），不引 DOM，
 *     因此可以在 Node 里用 `npm run verify` 完整测试。
 *   - 统计分两类：
 *       stats（累计，跨局持久化）：总击杀、总通关、各关卡通关次数……
 *       run （单局，开局清零）  ：本局建塔数、最高塔等级、技能次数……
 *     evaluate() 把两者合并成一份扁平视图再喂给谓词，谓词不必关心来源。
 *   - 存储读写全部走 Storage 封装，隐私模式下退化为内存，不会抛异常。
 */

import { readJson, writeJson } from './Storage.js';

const STORAGE_KEY = 'mechguard.achievements.v1';

/**
 * 成就定义
 *
 * check(s) 中的 s 是「累计统计 + 本局统计」的合并视图，可用字段见
 * _defaultStats() 与 _freshRun() 的注释。
 */
export const ACHIEVEMENTS = [
  {
    id: 'first_blood',
    name: '首战告捷',
    desc: '击杀第一个敌人',
    icon: '🎯',
    check: s => s.totalKills >= 1,
  },
  {
    id: 'hunter_100',
    name: '百人斩',
    desc: '累计击杀 100 个敌人',
    icon: '⚔️',
    check: s => s.totalKills >= 100,
  },
  {
    id: 'hunter_1000',
    name: '千人斩',
    desc: '累计击杀 1000 个敌人',
    icon: '💀',
    check: s => s.totalKills >= 1000,
  },
  {
    id: 'builder_10',
    name: '塔防建筑师',
    desc: '单局建造 10 座防御塔',
    icon: '🏗️',
    check: s => s.towersBuilt >= 10,
  },
  {
    id: 'max_tower',
    name: '满级大师',
    desc: '单局把一座防御塔升到 Lv3',
    icon: '⭐',
    check: s => s.maxTowerLevel >= 3,
  },
  {
    id: 'no_leak_10',
    name: '兵不血刃',
    desc: '不损失任何生命守住 10 波',
    icon: '🛡️',
    check: s => s.wavesCleared >= 10 && s.livesLost === 0,
  },
  {
    id: 'perfect_run',
    name: '完美防守',
    desc: '满生命通关任意关卡',
    icon: '💎',
    check: s => s.victory && s.livesLost === 0,
  },
  {
    id: 'shield_wall',
    name: '铜墙铁壁',
    desc: '单局用护盾吸收 5 次突破',
    icon: '🔰',
    check: s => s.shieldAbsorbs >= 5,
  },
  {
    id: 'skill_master',
    name: '技能大师',
    desc: '单局释放 10 次特殊技能',
    icon: '⚡',
    check: s => s.skillsCast >= 10,
  },
  {
    id: 'orbital_strike',
    name: '轨道打击者',
    desc: '一次轨道打击命中 5 个敌人',
    icon: '☄️',
    check: s => s.orbitalMaxHits >= 5,
  },
  {
    id: 'dragon_slayer',
    name: '屠龙者',
    desc: '击杀第一个 BOSS',
    icon: '🐉',
    check: s => s.totalBossKills >= 1,
  },
  {
    id: 'boss_hunter',
    name: 'BOSS 终结者',
    desc: '累计击杀 10 个 BOSS',
    icon: '👑',
    check: s => s.totalBossKills >= 10,
  },
  {
    id: 'witness_omega',
    name: '终焉见证者',
    desc: '见证 BOSS 进入「终焉」形态',
    icon: '💥',
    check: s => s.bossPhaseMax >= 3,
  },
  {
    id: 'rich',
    name: '富可敌国',
    desc: '单局持有 2000 金币',
    icon: '💰',
    check: s => s.goldPeak >= 2000,
  },
  {
    id: 'win_outpost',
    name: '前哨站英雄',
    desc: '通关「前哨站」',
    icon: '🛰️',
    check: s => (s.levelsWon?.outpost || 0) >= 1,
  },
  {
    id: 'win_canyon',
    name: '峡谷征服者',
    desc: '通关「峡谷通道」',
    icon: '🏜️',
    check: s => (s.levelsWon?.canyon || 0) >= 1,
  },
  {
    id: 'win_reactor',
    name: '反应堆主宰',
    desc: '通关「核心反应堆」',
    icon: '☢️',
    check: s => (s.levelsWon?.reactor || 0) >= 1,
  },
  {
    id: 'all_levels',
    name: '全境守护',
    desc: '通关全部三张关卡',
    icon: '🏆',
    check: s =>
      (s.levelsWon?.outpost || 0) >= 1 &&
      (s.levelsWon?.canyon || 0) >= 1 &&
      (s.levelsWon?.reactor || 0) >= 1,
  },
  {
    id: 'speedrun',
    name: '极速通关',
    desc: '30 分钟内通关任意关卡',
    icon: '⏱️',
    check: s => s.victory && s.elapsed > 0 && s.elapsed <= 1800,
  },
  {
    id: 'veteran',
    name: '久经沙场',
    desc: '累计完成 10 局游戏',
    icon: '🎖️',
    check: s => s.totalGames >= 10,
  },
];

/** 累计统计的初始值（跨局保留） */
function _defaultStats() {
  return {
    totalKills: 0,
    totalBossKills: 0,
    totalWavesCleared: 0,
    totalGames: 0,
    totalVictories: 0,
    totalSkills: 0,
    bestScore: 0,
    /** @type {Record<string, number>} 各关卡通关次数 */
    levelsWon: {},
  };
}

export class AchievementSystem {
  /**
   * @param {object} [opts]
   * @param {{getItem:Function,setItem:Function}|null} [opts.storage]
   * @param {(achievement:object)=>void} [opts.onUnlock] 解锁回调
   */
  constructor({ storage = null, onUnlock = null } = {}) {
    this.storage = storage;
    this.onUnlock = onUnlock;
    this._dirty = false;
    this._saveTimer = null;

    const loaded = readJson(storage, STORAGE_KEY, null);

    this._unlocked = (loaded && typeof loaded.unlocked === 'object' && loaded.unlocked)
      ? Object.assign({}, loaded.unlocked)
      : {};

    this._stats = Object.assign(
      _defaultStats(),
      (loaded && typeof loaded.stats === 'object' && loaded.stats) ? loaded.stats : {}
    );

    // levelsWon 必须是对象，脏存档可能把它写成别的类型
    if (!this._stats.levelsWon || typeof this._stats.levelsWon !== 'object') {
      this._stats.levelsWon = {};
    }

    this._run = this._freshRun();
  }

  /* ===================================================
   * 单局统计
   * =================================================== */

  _freshRun() {
    return {
      // 关卡信息
      levelId: '',
      difficulty: 1,

      // 本局行为
      towersBuilt: 0,
      maxTowerLevel: 1,
      skillsCast: 0,
      orbitalMaxHits: 0,
      shieldAbsorbs: 0,

      // 本局状态
      gold: 0,
      goldPeak: 0,
      lives: 0,
      livesMax: 0,
      livesLost: 0,
      wavesCleared: 0,
      bossPhaseMax: 0,

      // 结算
      victory: false,
      finished: false,
      elapsed: 0,
      score: 0,
    };
  }

  /** 开始新一局：清空单局统计，但保留累计统计 */
  startRun({ levelId = '', difficulty = 1, lives = 0, gold = 0 } = {}) {
    this._run = this._freshRun();
    this._run.levelId = levelId;
    this._run.difficulty = difficulty;
    this._run.lives = lives;
    this._run.livesMax = lives;
    this._run.gold = gold;
    this._run.goldPeak = gold;
  }

  /** 单局统计对象（只读用途） */
  get run() {
    return this._run;
  }

  /** 累计统计对象（只读用途） */
  get stats() {
    return this._stats;
  }

  /* ===================================================
   * 写入
   * =================================================== */

  /**
   * 高频写入防抖：仅标记 dirty，~600ms 后合并成一次真正落盘。
   * 避免「每击杀一个敌人就同步写一次 localStorage」带来的主线程卡顿。
   */
  _scheduleSave() {
    this._dirty = true;
    if (this._saveTimer !== null) return;
    this._saveTimer = setTimeout(() => {
      this._saveTimer = null;
      if (this._dirty) this._save();
    }, 600);
  }

  /** 强制立即落盘（结算时 / 页面隐藏时调用，确保不丢数据） */
  flush() {
    if (this._saveTimer !== null) {
      clearTimeout(this._saveTimer);
      this._saveTimer = null;
    }
    if (this._dirty) this._save();
  }

  /** 累计计数 +n（合并落盘） */
  bump(key, n = 1) {
    if (!(key in this._stats)) return;
    this._stats[key] = (this._stats[key] || 0) + n;
    this._scheduleSave();
  }

  /** 设置累计值（取较大者，用于 bestScore 这类「只升不降」的指标），合并落盘 */
  bumpMax(key, value) {
    if (!(key in this._stats)) return;
    if (value > (this._stats[key] || 0)) {
      this._stats[key] = value;
      this._scheduleSave();
    }
  }

  /** 记录某关卡通关一次（通关事件很少，直接落盘以保证持久化即时生效） */
  recordLevelWin(levelId) {
    if (!levelId) return;
    this._stats.levelsWon[levelId] = (this._stats.levelsWon[levelId] || 0) + 1;
    this._save();
  }

  /** 单局计数 +n */
  runAdd(key, n = 1) {
    if (!(key in this._run)) return;
    this._run[key] = (this._run[key] || 0) + n;
  }

  /** 单局取最大值 */
  runMax(key, value) {
    if (!(key in this._run)) return;
    if (value > (this._run[key] || 0)) this._run[key] = value;
  }

  /** 单局直接赋值 */
  runSet(key, value) {
    if (!(key in this._run)) return;
    this._run[key] = value;
  }

  /* ===================================================
   * 评估
   * =================================================== */

  /** 累计 + 单局的合并视图（喂给谓词） */
  get view() {
    return Object.assign({}, this._stats, this._run);
  }

  /**
   * 检查是否有新成就达成
   * @returns {object[]} 本次新解锁的成就（按定义顺序）
   */
  evaluate() {
    const view = this.view;
    const newly = [];

    for (const achievement of ACHIEVEMENTS) {
      if (this._unlocked[achievement.id]) continue;

      let ok = false;
      try {
        ok = !!achievement.check(view);
      } catch {
        // 谓词出错不应影响游戏，当作未达成
        ok = false;
      }

      if (ok) {
        this._unlocked[achievement.id] = Date.now();
        newly.push(achievement);
      }
    }

    if (newly.length > 0) {
      this._save();
      if (this.onUnlock) {
        for (const a of newly) this.onUnlock(a);
      }
    }

    return newly;
  }

  /* ===================================================
   * 查询
   * =================================================== */

  isUnlocked(id) {
    return !!this._unlocked[id];
  }

  get unlockedCount() {
    return ACHIEVEMENTS.reduce((n, a) => n + (this._unlocked[a.id] ? 1 : 0), 0);
  }

  get total() {
    return ACHIEVEMENTS.length;
  }

  /** 供 UI 渲染：每条成就附带解锁状态与时间 */
  get list() {
    return ACHIEVEMENTS.map(a => ({
      id: a.id,
      name: a.name,
      desc: a.desc,
      icon: a.icon,
      unlocked: !!this._unlocked[a.id],
      unlockedAt: this._unlocked[a.id] || 0,
    }));
  }

  /* ===================================================
   * 维护
   * =================================================== */

  /** 清空所有成就与累计统计（调试 / 玩家重置用） */
  resetAll() {
    this._unlocked = {};
    this._stats = _defaultStats();
    this._run = this._freshRun();
    this._save();
  }

  _save() {
    this._dirty = false;
    if (this._saveTimer !== null) {
      clearTimeout(this._saveTimer);
      this._saveTimer = null;
    }
    writeJson(this.storage, STORAGE_KEY, {
      unlocked: this._unlocked,
      stats: this._stats,
    });
  }
}
